import { decodeHtmlEntities } from './utils';

const CTA_BRACKET_PATTERN = /\[(Primary|Secondary)\s+CTA:\s*([^\]]+?)\]/gi;
const CTA_BUTTON_PATTERN = /\[Button:\s*([^\]]+?)\]/gi;
const CTA_LINE_PATTERN = /(^|\n)\s*(Primary|Secondary)\s+CTA:\s*(.+?)\s*(?=\n|$)/gi;
const CTA_PARAGRAPH_PATTERN = /<p>([\s\S]*?)<\/p>/gi;
const CTA_ONLY_PARAGRAPH_PATTERN = /<p>\s*((?:<a[^>]*data-cta-variant[^>]*>[^<]*<\/a>\s*)+)<\/p>/gi;
const CTA_SECTION_LIST_PATTERN = /(<p>\s*<strong>\s*CTA Section\s*<\/strong>\s*<\/p>)\s*<ul>([\s\S]*?)<\/ul>/gi;
const CTA_SECTION_INLINE_PATTERN = /<p>\s*<strong>\s*CTA Section\s*<\/strong>\s*(?:<br\s*\/?>(?:\s|&nbsp;)*)+([\s\S]*?)<\/p>/gi;
const CTA_SECTION_NEXT_PARAGRAPH_PATTERN = /<p>\s*<strong>\s*CTA Section\s*<\/strong>\s*<\/p>\s*<p>([\s\S]*?)<\/p>/gi;
const NEXT_STEPS_INLINE_PATTERN = /<p>\s*<strong>\s*Next Steps\s*<\/strong>\s*(?:<br\s*\/?>(?:\s|&nbsp;)*)+([\s\S]*?)<\/p>/gi;
const NEXT_STEPS_NEXT_PARAGRAPH_PATTERN = /<p>\s*<strong>\s*Next Steps\s*<\/strong>\s*<\/p>\s*<p>([\s\S]*?)<\/p>/gi;
const CTA_LINK_SEPARATOR = '&#8203;';

const escapeHtml = (value = '') => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const buildCtaLinkHtml = (variant, label) => {
  const normalizedVariant = String(variant || '').toLowerCase() === 'secondary' ? 'secondary' : 'primary';
  const normalizedLabel = decodeHtmlEntities(String(label || '')).replace(/\s+/g, ' ').trim();
  const safeLabel = escapeHtml(normalizedLabel);

  if (!safeLabel) {
    return '';
  }

  return `<a class="generated-cta-link generated-cta-link--${normalizedVariant}" data-cta-variant="${normalizedVariant}">${safeLabel}</a>`;
};

const convertParagraphContentToLinks = (content = '') => {
  const trimmedContent = String(content || '').trim();

  if (!trimmedContent) {
    return content;
  }

  const lineMatches = [...trimmedContent.matchAll(/^(Primary|Secondary)\s+CTA:\s*(.+)$/gim)];
  if (!lineMatches.length) {
    return content;
  }

  const normalizedText = trimmedContent.replace(/<br\s*\/?>/gi, '\n').replace(/&nbsp;/gi, ' ');
  const converted = normalizedText.replace(
    CTA_LINE_PATTERN,
    (_match, prefix, variant, label) => `${prefix}${buildCtaLinkHtml(variant, label)}`,
  ).trim();

  return converted.includes('<a') ? converted : content;
};

const stripHtmlTags = (value = '') => decodeHtmlEntities(String(value || '').replace(/<[^>]*>/g, '')).trim();

const parseCtaVariant = (attributes = '', fallback = 'primary') => {
  const variantMatch = String(attributes || '').match(/data-cta-variant\s*=\s*['\"](primary|secondary)['\"]/i);
  if (variantMatch?.[1]) {
    return variantMatch[1].toLowerCase();
  }
  return fallback;
};

const parseHref = (attributes = '') => {
  const hrefMatch = String(attributes || '').match(/\shref\s*=\s*['\"]([^'\"]*)['\"]/i);
  return hrefMatch?.[1]?.trim() || null;
};

const extractTextLines = (value = '') => String(value || '')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/p>/gi, '\n')
  .replace(/<p[^>]*>/gi, '')
  .replace(/<[^>]*>/g, '')
  .split('\n')
  .map((line) => line.replace(/&nbsp;/gi, ' ').trim())
  .filter(Boolean);

export const extractCtaButtonsFromHtml = (html = '') => {
  const source = String(html || '');
  const buttons = [];

  const anchorRegex = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;
  let anchorMatch;
  while ((anchorMatch = anchorRegex.exec(source)) !== null) {
    const attributes = anchorMatch[1] || '';
    if (!/data-cta-variant/i.test(attributes) && !/generated-cta-link/i.test(attributes)) {
      continue;
    }

    const text = stripHtmlTags(anchorMatch[2] || '');
    if (!text) {
      continue;
    }

    buttons.push({
      text,
      url: parseHref(attributes),
      variant: parseCtaVariant(attributes, buttons.length === 0 ? 'primary' : 'secondary'),
    });
  }

  if (buttons.length > 0) {
    return buttons;
  }

  const bracketRegex = /\[(Primary|Secondary)\s+CTA:\s*([^\]]+?)\]|\[Button:\s*([^\]]+?)\]/gi;
  let bracketMatch;
  while ((bracketMatch = bracketRegex.exec(source)) !== null) {
    const explicitVariant = bracketMatch[1] ? bracketMatch[1].toLowerCase() : null;
    const text = String(bracketMatch[2] || bracketMatch[3] || '').trim();
    if (!text) {
      continue;
    }

    buttons.push({
      text,
      url: null,
      variant: explicitVariant || (buttons.length === 0 ? 'primary' : 'secondary'),
    });
  }

  if (buttons.length > 0) {
    return buttons;
  }

  let nextStepsMatch;
  while ((nextStepsMatch = NEXT_STEPS_INLINE_PATTERN.exec(source)) !== null) {
    const lines = extractTextLines(nextStepsMatch[1]);
    const firstLine = lines[0];
    if (firstLine) {
      buttons.push({
        text: firstLine,
        url: null,
        variant: 'primary',
      });
    }
  }

  if (buttons.length > 0) {
    return buttons;
  }

  while ((nextStepsMatch = NEXT_STEPS_NEXT_PARAGRAPH_PATTERN.exec(source)) !== null) {
    const lines = extractTextLines(nextStepsMatch[1]);
    const firstLine = lines[0];
    if (firstLine) {
      buttons.push({
        text: firstLine,
        url: null,
        variant: 'primary',
      });
    }
  }

  return buttons;
};

export const convertHtmlCtasToLinks = (html = '') => {
  const stringHtml = String(html || '');
  const alreadyContainsCtaLinks = /<a[^>]*class="generated-cta-link/i.test(stringHtml);

  let buttonIndex = 0;
  const withBracketedButtons = alreadyContainsCtaLinks
    ? stringHtml
    : stringHtml.replace(
      CTA_BUTTON_PATTERN,
      (_match, label) => buildCtaLinkHtml(buttonIndex++ === 0 ? 'primary' : 'secondary', label),
    );

  const withBracketedLinks = alreadyContainsCtaLinks
    ? stringHtml
    : withBracketedButtons.replace(
      CTA_BRACKET_PATTERN,
      (_match, variant, label) => buildCtaLinkHtml(variant, label),
    );

  const withParagraphLinks = withBracketedLinks.replace(
    CTA_PARAGRAPH_PATTERN,
    (_match, paragraphContent) => {
      const converted = convertParagraphContentToLinks(paragraphContent);
      return converted === paragraphContent ? _match : converted;
    },
  );

  const unwrappedLinks = withParagraphLinks.replace(
    CTA_ONLY_PARAGRAPH_PATTERN,
    (_match, links) => links.trim(),
  );

  const normalizedSectionLists = unwrappedLinks.replace(
    CTA_SECTION_LIST_PATTERN,
    (match, heading, listContent) => {
      const anchors = String(listContent || '').match(/<a\b[^>]*(?:data-cta-variant|generated-cta-link)[^>]*>[\s\S]*?<\/a>/gi);
      if (!anchors || anchors.length === 0) {
        return match;
      }
      return `${heading}<p>${anchors.join('')}</p>`;
    },
  );

  const normalizedSectionInline = normalizedSectionLists.replace(
    CTA_SECTION_INLINE_PATTERN,
    (match, ctaTextContent) => {
      const lines = extractTextLines(ctaTextContent);
      const validLines = lines.filter((line) => !/^\[button:/i.test(line));
      if (!validLines.length) {
        return match;
      }

      const links = validLines.slice(0, 2).map((line, index) => buildCtaLinkHtml(index === 0 ? 'primary' : 'secondary', line));
      return `<p><strong>CTA Section</strong></p><p>${links.join('')}</p>`;
    },
  );

  const normalizedSectionParagraph = normalizedSectionInline.replace(
    CTA_SECTION_NEXT_PARAGRAPH_PATTERN,
    (match, sectionTextContent) => {
      if (/<a\b[^>]*data-cta-variant/i.test(sectionTextContent)) {
        return match;
      }

      const lines = extractTextLines(sectionTextContent);
      const validLines = lines.filter((line) => line && !/^\[button:/i.test(line));
      if (!validLines.length) {
        return match;
      }

      const links = validLines.slice(0, 2).map((line, index) => buildCtaLinkHtml(index === 0 ? 'primary' : 'secondary', line));
      return `<p><strong>CTA Section</strong></p><p>${links.join('')}</p>`;
    },
  );

  const normalizedNextStepsInline = normalizedSectionParagraph.replace(
    NEXT_STEPS_INLINE_PATTERN,
    (match, ctaTextContent) => {
      const lines = extractTextLines(ctaTextContent);
      const firstLine = lines[0];
      if (!firstLine) {
        return match;
      }

      const link = buildCtaLinkHtml('primary', firstLine);
      return `<p><strong>Next Steps</strong></p><p>${link}</p>`;
    },
  );

  const normalizedNextStepsBlocks = normalizedNextStepsInline.replace(
    NEXT_STEPS_NEXT_PARAGRAPH_PATTERN,
    (match, blockContent) => {
      const lines = extractTextLines(blockContent);
      const firstLine = lines[0];
      if (!firstLine) {
        return match;
      }

      const link = buildCtaLinkHtml('primary', firstLine);
      return `<p><strong>Next Steps</strong></p><p>${link}</p>`;
    },
  );

  return normalizedNextStepsBlocks.replace(
    /(<a[^>]*data-cta-variant[^>]*>[^<]*<\/a>)\s*(?=<a[^>]*data-cta-variant[^>]*>)/gi,
    `$1${CTA_LINK_SEPARATOR}`,
  );
};